import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Target, ChevronRight, Loader2, TrendingUp } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { getArea, formatDMY } from "./shared";

export default function AreaDetailView({ areaName, bep }) {
  const meta = getArea(areaName);
  const goals = (bep?.goals || []).filter((g) => g.area === areaName);
  const areaSummary = (bep?.summary?.areas || []).find((a) => a.area === areaName);
  const hex = areaSummary?.color || meta?.hex || "#7C3AED";
  const avg = areaSummary?.avg_progress ?? (goals.length ? Math.round(goals.reduce((s, g) => s + (g.progress || 0), 0) / goals.length) : 0);

  const [openGoal, setOpenGoal] = useState(null);
  const [series, setSeries] = useState(null); // null=loading, []=empty

  const openChart = async (g) => {
    setOpenGoal(g);
    setSeries(null);
    try {
      const { data } = await api.get(`/parent/bep/goals/${g.id}/progress`);
      const rows = (data || []).map((d) => ({ date: formatDMY(d.date), progress: d.progress }));
      setSeries(rows.length ? rows : (g.progress != null ? [{ date: "Güncel", progress: g.progress }] : []));
    } catch (e) {
      setSeries([]);
    }
  };

  return (
    <div data-testid="parent-area-detail">
      {/* Kategori özet başlığı */}
      <Card className="flex items-center gap-4 rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: `${hex}1a` }}><meta.icon className="h-6 w-6" style={{ color: hex }} /></div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate font-heading text-xl font-bold text-slate-900">{areaName}</h1>
          <p className="text-sm text-slate-500">{goals.length} hedef · Ortalama %{avg}</p>
        </div>
        <span className="text-2xl font-extrabold" style={{ color: hex }}>%{avg}</span>
      </Card>

      <h2 className="mb-2 mt-6 font-heading font-bold text-slate-900">Hedefler</h2>
      <p className="mb-3 text-xs text-slate-400">Gelişim grafiğini görmek için bir hedefe dokunun.</p>
      <div className="space-y-3">
        {goals.map((g) => {
          const p = g.progress || 0;
          const subs = g.subgoals || [];
          const doneCount = subs.filter((s) => s.done).length;
          return (
            <Card
              key={g.id}
              data-testid={`area-goal-${g.id}`}
              onClick={() => openChart(g)}
              className="cursor-pointer rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)] transition-transform hover:-translate-y-0.5 active:scale-[0.99]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold leading-snug text-slate-900">{g.title}</p>
                  {g.description && <p className="mt-0.5 text-sm text-slate-500">{g.description}</p>}
                  {subs.length > 0 && <p className="mt-1 text-xs text-slate-400">{doneCount}/{subs.length} alt hedef tamam</p>}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold" style={{ color: hex }}>%{p}</span>
                  <ChevronRight className="h-5 w-5 text-slate-300" />
                </div>
              </div>
              <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, backgroundColor: hex }} />
              </div>
            </Card>
          );
        })}
        {goals.length === 0 && (
          <Card className="rounded-2xl border-dashed p-14 text-center text-slate-400"><Target className="mx-auto mb-3 h-9 w-9 text-slate-300" />Bu alanda henüz hedef yok.</Card>
        )}
      </div>

      {/* Hedef bazlı mini gelişim grafiği */}
      <Dialog open={!!openGoal} onOpenChange={(o) => { if (!o) { setOpenGoal(null); setSeries(null); } }}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="goal-chart-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6 font-heading text-base leading-snug">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${hex}1a` }}><TrendingUp className="h-4 w-4" style={{ color: hex }} /></span>
              <span className="min-w-0">{openGoal?.title}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="-mt-1 text-xs text-slate-400">{areaName} · Tarih bazlı ilerleme</p>
          <div className="mt-2 rounded-2xl bg-slate-50/70 p-3 pt-5" data-testid="goal-chart-body">
            {series === null ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
            ) : series.length === 0 ? (
              <p className="py-14 text-center text-sm text-slate-400">Bu hedef için henüz ilerleme kaydı yok.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={series} margin={{ top: 6, right: 14, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={34} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v) => [`%${v}`, "İlerleme"]}
                  />
                  <Line type="monotone" dataKey="progress" stroke={hex} strokeWidth={2.5} dot={{ r: 3.5, fill: hex, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          {series && series.length > 0 && (
            <div className="flex items-center justify-between px-1 text-sm">
              <span className="text-slate-500">Güncel ilerleme</span>
              <span className="font-bold" style={{ color: hex }}>%{openGoal?.progress ?? series[series.length - 1].progress}</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
